import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import {
    ArrowUpRight,
    Clock3,
    MapPin,
    MessageSquareText,
    Phone,
    Siren
} from 'lucide-react'
import { pad } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { PageHero } from '@/components/generals/page-hero'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { TransitionLink } from '@/components/generals/transition-link'
import { Reveal } from '@/components/motion/reveal'
import { ContactForm } from '@/components/widgets/contact-form'
import { contactFaqs } from '@/constants/contact'
import { siteConfig, QUOTE_PATH } from '@/constants/site'

const channels: { icon: LucideIcon, label: string, value: string, note: string, href?: string }[] = [
    { icon: Phone, label: "Call the office", value: siteConfig.phone, note: siteConfig.hours, href: siteConfig.phoneHref },
    { icon: MessageSquareText, label: "Send a text", value: siteConfig.phone, note: "Photos of the door welcome", href: siteConfig.smsHref },
    { icon: Siren, label: "Door stuck right now", value: siteConfig.emergencyHours, note: "Same-day visits across the metro", href: siteConfig.phoneHref },
    { icon: MapPin, label: "Visit the yard", value: siteConfig.address, note: "Door samples on show" },
    { icon: Clock3, label: "Message replies", value: "Same day", note: "Usually within two hours" }
]

const ContactPage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Contact'
                description="Call, text or message Liftline Door Co. Office open Mon–Sat 8 AM–6 PM, emergency line until 10 PM, and same-day replies to every message."
            />

            <PageHero
                eyebrow="Contact"
                title="Talk to a real person about your door."
                accent={['real', 'person']}
                description="Ask about a repair, a new door or a booking you already have. Our office team answers every message themselves, usually within two hours."
                actions={(
                    <>
                        <ButtonLink href='#message' size='lg'>Write to us</ButtonLink>
                        <ButtonLink href={siteConfig.phoneHref} variant='outline' size='lg' icon='phone'>{siteConfig.phone}</ButtonLink>
                    </>
                )}
                aside={(
                    <div className='relative overflow-hidden rounded-[28px] bg-ink p-7 text-cream sm:p-9'>
                        <div className='door-grooves pointer-events-none absolute inset-0 opacity-50' aria-hidden='true' />

                        <div className='relative'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/60'>How we answer</p>

                            <ol className='mt-6 space-y-5'>
                                {[
                                    ["Message", "Same day, by email or a call back"],
                                    ["Phone", "Answered by the office, not a call center"],
                                    ["Emergency", `Line open until ${siteConfig.emergencyUntil}`]
                                ].map(([title, body], index) => (
                                    <li key={title} className='flex gap-4 border-t border-cream/10 pt-5 first:border-t-0 first:pt-0'>
                                        <span className='font-mono text-[12px] text-ember-soft'>{pad(index + 1)}</span>

                                        <span>
                                            <span className='block font-heading text-[1.2rem] font-semibold tracking-[-0.02em]'>{title}</span>
                                            <span className='mt-1 block text-[14.5px] text-cream/70'>{body}</span>
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                )}
            />

            <section id='message' className='scroll-mt-28 pb-24 md:pb-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <div className='lg:sticky lg:top-32'>
                            <Reveal selector=':scope > li' as='ul' className='grid gap-px overflow-hidden rounded-[28px] border border-line bg-line sm:grid-cols-2 lg:grid-cols-1'>
                                {channels.map(channel => {
                                    const content = (
                                        <>
                                            <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-sand text-ink transition-colors group-hover:bg-ember group-hover:text-white'>
                                                <channel.icon className='size-4' aria-hidden='true' />
                                            </span>

                                            <span className='min-w-0 flex-1'>
                                                <span className='block font-mono text-[11px] uppercase tracking-[0.16em] text-slate'>{channel.label}</span>
                                                <span className='mt-1.5 block text-[16px] font-medium leading-snug text-ink'>{channel.value}</span>
                                                <span className='mt-1 block text-[13.5px] text-slate'>{channel.note}</span>
                                            </span>

                                            {channel.href && (
                                                <ArrowUpRight className='size-4 shrink-0 text-slate transition-[color,transform,translate,scale,rotate] duration-300 group-hover:rotate-45 group-hover:text-ink' aria-hidden='true' />
                                            )}
                                        </>
                                    )

                                    return (
                                        <li key={channel.label} className='bg-card'>
                                            {channel.href ? (
                                                <a href={channel.href} className='group flex h-full items-start gap-4 p-6 transition-colors hover:bg-paper'>
                                                    {content}
                                                </a>
                                            ) : (
                                                <div className='flex h-full items-start gap-4 p-6'>{content}</div>
                                            )}
                                        </li>
                                    )
                                })}
                            </Reveal>

                            <div className='mt-6 rounded-[24px] bg-ember-tint p-6'>
                                <p className='font-heading text-[1.25rem] font-semibold tracking-[-0.02em] text-ink'>After a price instead?</p>

                                <p className='mt-2 text-[14.5px] leading-relaxed text-slate'>
                                    The quote form asks the right questions for your door and texts a written price range back, usually within 30 minutes.
                                </p>

                                <TransitionLink
                                    href={`${QUOTE_PATH}?entry=contact_page`}
                                    className='mt-4 inline-flex items-center gap-2 text-[14.5px] font-medium text-ember underline-offset-4 hover:underline'
                                >
                                    Get my written price
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                </TransitionLink>
                            </div>
                        </div>
                    </div>

                    <div className='lg:col-span-7'>
                        <ContactForm />
                    </div>
                </Container>
            </section>

            <section id='contact-faq' className='bg-sand/60 py-24 md:py-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <SectionHeading
                            eyebrow="Before you write"
                            title="Quick answers to common questions."
                            layout='stack'
                        />
                    </div>

                    <Reveal selector=':scope > li' as='ul' className='border-t border-ink/10 lg:col-span-7'>
                        {contactFaqs.map((faq, index) => (
                            <li key={faq.question} className='grid gap-3 border-b border-ink/10 py-8 md:grid-cols-12 md:gap-6'>
                                <h3 className='flex items-baseline gap-4 font-heading text-[1.3rem] font-semibold leading-tight tracking-[-0.025em] text-ink md:col-span-5'>
                                    <span className='font-mono text-[12px] font-normal tracking-normal text-slate'>{pad(index + 1)}</span>
                                    {faq.question}
                                </h3>

                                <p className='text-[15.5px] leading-relaxed text-slate md:col-span-7'>{faq.answer}</p>
                            </li>
                        ))}
                    </Reveal>
                </Container>
            </section>

            <CtaBand
                title="See the price before we reach for a tool."
                body="Describe the door and we'll text a price range back, usually within 30 minutes."
                primary={{ label: "Request a price", href: QUOTE_PATH }}
            />
        </Layout>
    )
}

export default ContactPage
